import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css, tw } from 'twind/css';
import { Button, Icon } from '@global-ecom/nitro-uds/elements';

import { FilterOption, SortOption } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useSticky } from 'hooks/useSticky';
import { ProductListSort } from 'ui/components/ProductListFilterSort';
import { ProductListActiveFilters } from 'ui/components/ProductListActiveFilters';
import { ProductListFilterDropdown } from 'ui/components/ProductListFilterDropdown';
import { ProductListFilterDrawer } from 'ui/components/ProductListFilterDrawer';
import { useProductListFilterContext } from 'ui/components/ProductListFilterProvider';
import useAvailableSpace from 'hooks/useAvailableSpace';
import { removePriceFilterIfMinMaxExists } from 'utils/priceFilter';
import { useSiteConfig } from 'hooks/useSiteConfig';

export type FilterProps = {
  enabledFilterOptions: FilterOption[];
  sortOptions: SortOption[];
  defaultSortOption: string;
};

const MemoComponent = (props: FilterProps) => {
  const t = useTranslate();
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [hidden, setHidden] = useState<boolean>(false);
  const [lastScroll, setLastScroll] = useState<number>(0);
  const filterListRef = useRef<HTMLDivElement>(null);
  const { staticFeatures } = useSiteConfig();
  const { totalCount, filterItemsCache } = useProductListFilterContext();

  const filterItems = useMemo(
    () => filterItemsCache.filter(items => items && items.values.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filterItemsCache)]
  );

  const { items } = useAvailableSpace<HTMLDivElement, FilterOption>(
    filterListRef,
    filterItems
  );

  const allFiltersFit = items.filter(i => i.hidden).length === 0;

  const [shouldUseFiltersDrawer, setShouldUseFiltersDraw] = useState(false);
  const { stickyRef, stickyTop, stickyWidth, stickyCss } = useSticky(2);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  useEffect(() => {
    setShouldUseFiltersDraw(stickyWidth < 960);
  }, [stickyWidth]);

  const handleScroll = useCallback(() => {
    const bounds = stickyRef.current?.getBoundingClientRect();
    if (
      bounds &&
      !hidden &&
      // * Opening the mobile menu sets the lastScroll as 0
      // * we want to ignore this as the scroll will always be > 0
      // * once the filters become sticky
      lastScroll !== 0 &&
      window.scrollY > lastScroll &&
      bounds.top <= stickyTop
    ) {
      setHidden(true);
      // * If we're at the top of the page we don't need to show the filters via this
    } else if (hidden && window.scrollY !== 0 && window.scrollY < lastScroll) {
      setHidden(false);
    }

    setLastScroll(window.scrollY);
  }, [hidden, lastScroll, stickyRef, stickyTop]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!shouldUseFiltersDrawer) {
      setFiltersDrawerOpen(false);
    }
  }, [shouldUseFiltersDrawer]);

  const _filterItems = useMemo(
    () =>
      removePriceFilterIfMinMaxExists(
        filterItems,
        staticFeatures.isPriceFilterSliderEnabled
      ),
    [filterItems, staticFeatures.isPriceFilterSliderEnabled]
  );

  useEffect(() => {
    if (filtersDrawerOpen)
      // Disable scroll when the drawer is open
      document.body.style.overflowY = 'hidden';
    else {
      document.body.style.overflowY = 'auto';
    }
  }, [filtersDrawerOpen]);

  return props.enabledFilterOptions.length === 0 ? null : (
    <>
      <nav
        ref={stickyRef}
        className={tw(
          `z-10 -mx-auto relative transform transition-transform duration-300`,
          stickyCss,
          hidden && '-translate-y-full lg:transform-y-0'
        )}
      >
        <div
          aria-label={t('productFilters')}
          data-test-id="filter-drawer"
          className={tw(
            'relative z-10 bg-white ',
            'before:absolute before:top-0 before:w-screen before:h-full',
            'before:left-1/2 before:-translate-x-1/2',
            'before:border-b before:border-t',
            css`
              &::before {
                content: ' ';
                pointer-events: none;
              }
            `
          )}
        >
          <div className="max-w-screen-3xl w-full mx-auto">
            <div className="py-5 flex">
              <div className="flex items-center flex-1 space-x-2 lg:space-x-3">
                <div className={tw('flex', allFiltersFit && 'lg:hidden')}>
                  <Button
                    data-test-id="open-filters-btn"
                    variant="secondary"
                    icon="filter"
                    iconEnd
                    label={`${t('filters')}`}
                    onClick={() => setFiltersDrawerOpen(!filtersDrawerOpen)}
                  />
                </div>

                <div className="hidden lg:flex items-center flex-1">
                  <h2 className={tw('mr-3', !allFiltersFit && 'hidden')}>
                    <Icon name="filter" size="2xl" label={t('filters')} />
                  </h2>
                  <div
                    className={tw(
                      `flex w-1 flex-1`,
                      !allFiltersFit && 'invisible'
                    )}
                    ref={filterListRef}
                  >
                    {_filterItems.map(item => (
                      <ProductListFilterDropdown
                        key={item.id}
                        data={item}
                        openFilter={openFilter}
                        setOpenFilter={setOpenFilter}
                        classNames="mr-3"
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                {props.sortOptions.length > 0 && (
                  <ProductListSort
                    options={props.sortOptions}
                    defaultSortOption={props.defaultSortOption}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <ProductListActiveFilters />
      {filtersDrawerOpen && (
        <ProductListFilterDrawer
          onClose={() => setFiltersDrawerOpen(false)}
          items={_filterItems}
          totalCount={totalCount}
        />
      )}
    </>
  );
};

export const ProductListFilter: React.FC<FilterProps> = memo(MemoComponent);
